import { computed } from 'mobx';
import { Env } from 'src/core/Environment';
import { SettingsStore } from 'src/stores/SettingsStore/SettingsStore';
import { generateReadablePermissions } from 'src/stores/UserStore/UserPermissions.config';
import { UserRole } from 'src/stores/UserStore/UserStore.types';

type KeycloakTokenParsed = {
    realm_access?: { roles?: string[] };
    resource_access?: Record<string, { roles?: string[] }>;
};

/**
 * Identity & permissions of the current user (UX layer).
 *
 * Roles are read from the Keycloak access token — resource/client roles first
 * (`resource_access[appName]`), then realm roles (`realm_access`) — with no
 * separate API DTO and no local persistence. Permissions are derived from the
 * matrix via `can` / `canNot`.
 *
 * NOTE: these are UX-only checks; the backend is the authoritative enforcer.
 */
export class UserStoreClass {
    /** Current user's roles: resource_access[appName] first, then realm_access. */
    @computed get roles(): UserRole[] {
        const tokenParsed = SettingsStore.keycloak?.tokenParsed as
            | KeycloakTokenParsed
            | undefined;

        const realmAccessRoles = tokenParsed?.realm_access?.roles || [];

        const resourceKey = SettingsStore.settings?.appName || Env.appName;
        const resourceAccessRoles =
            tokenParsed?.resource_access?.[resourceKey]?.roles || [];

        return [...resourceAccessRoles, ...realmAccessRoles] as UserRole[];
    }

    /** Positive permission map: `UserStore.can.view.partners`, `can.manage.products`, … */
    @computed get can() {
        return generateReadablePermissions(this.roles, true);
    }

    /** Negated permission map: `UserStore.canNot.view.loanApplications` === !can.view.loanApplications */
    @computed get canNot() {
        return generateReadablePermissions(this.roles, false);
    }

    /** Case-insensitive role check. Prefer `can` / `canNot` in React code. */
    hasRole = (role: string): boolean =>
        this.roles.some(
            (userRole) => userRole.toLowerCase() === role.toLowerCase(),
        );

    /**
     * True when the user has AT LEAST ONE of the given roles.
     * Accepts an array: `UserStore.hasAnyRole([UserRoles.SalesOfficer])`.
     */
    hasAnyRole = (roles: UserRole[]): boolean =>
        roles.some((role) => this.hasRole(role));

    /**
     * Loads the Keycloak user profile. Optional for RBAC (roles come from the
     * token) — kept for parity with the reference lifecycle and future use.
     */
    async loadUserInfo() {
        try {
            await SettingsStore.keycloak?.loadUserProfile();
        } catch {
            // Profile is not required for RBAC — roles are read from the token.
        }
    }
}

export const UserStore = new UserStoreClass();
